import { useEffect } from "react";
import i18n from "i18next";
import { useAuth } from "@/lib/auth";
import { LOCALE_STORAGE_KEY, isSupportedLocale } from "@/lib/locales";

// Applies the viewer's preferred UI language in a post-mount effect (never
// during the initial render, so SSR'd pages don't hydrate-mismatch). That only
// holds while the server-rendered pages have no Suspense boundary above them:
// React hydrates such a boundary AFTER this effect has run, so the switch would
// land first and the page would render French against English server HTML
// (see AppRoutes.tsx and __tests__/ssr-hydration-parity.test.tsx). Priority:
// a signed-in user's saved `ui_locale` > an anonymous visitor's stored choice >
// their browser language. Falls through to English (the init default).
export default function LocaleSync() {
  const { user } = useAuth();
  const uiLocale = user?.uiLocale;

  useEffect(() => {
    const stored = typeof window !== "undefined" ? window.localStorage.getItem(LOCALE_STORAGE_KEY) : null;
    const browser = typeof window !== "undefined" ? window.navigator?.language?.slice(0, 2) : null;
    const desired = [uiLocale, stored, browser].find((c) => isSupportedLocale(c));
    if (desired && i18n.language !== desired) {
      i18n.changeLanguage(desired);
    }
  }, [uiLocale]);

  // Keep <html lang> in sync with the active locale so screen readers, browser
  // translation and spell-check use the right language (#172). Static lang="en"
  // in the layout was wrong for the other 16 locales.
  useEffect(() => {
    const apply = (lng: string) => {
      if (typeof document !== "undefined" && isSupportedLocale(lng)) {
        document.documentElement.lang = lng;
      }
    };
    apply(i18n.language);
    i18n.on("languageChanged", apply);
    return () => i18n.off("languageChanged", apply);
  }, []);

  return null;
}
